/**
 * Telling a directory request that was called off apart from one that failed,
 * and the parameters of the counts request.
 *
 * Kept free of Vue and of the HTTP client so it stays testable with Vitest,
 * like the rest of `@c/addressBook`.
 */

import { ADDRESS_BOOK_TABS } from '@/router/contacts/addressBookTabs'

/**
 * The code the HTTP client (`src/utils/http/createHttpClient.ts`) sets on the
 * error it rejects with when a request is aborted through its `signal`.
 *
 * Already the literal the HTTP client itself tests before deciding whether to
 * toast — `if (error.code === 'ERR_CANCELED') return Promise.reject(error)`
 * (`handleResponseError` in `apiClient.js`) — and the one every other screen that aborts in flight
 * tests too (`InstantSearchPalette.vue:373`, `ThirdPartyCostContent.vue:309`,
 * `ThreadListComponent.vue:174`). Named here so the address book does not spell
 * it a fifth time, and so the one place that has to recognise a cancellation
 * cannot drift from the one place that has to stay silent about it.
 *
 * @type {string}
 */
export const CANCELED_REQUEST_CODE = 'ERR_CANCELED'

/**
 * Whether a rejection is a request we called off ourselves.
 *
 * The distinction is the whole point: a cancellation is an outcome we asked
 * for, so it must leave no toast and no trace, while a genuine failure has to
 * keep behaving exactly as it did before — the interceptor toasts it, and the
 * caller clears its loading flag.
 *
 * Reads the code defensively because a rejection is not guaranteed to be an
 * axios error at all: a `TypeError` thrown inside a `then` handler arrives on
 * the same channel, and must never be mistaken for a cancellation and silently
 * swallowed.
 *
 * @param {{code?: string}|Error|null|undefined} reason
 * @returns {boolean}
 */
export function isCanceledRequest(reason) {
  return reason?.code === CANCELED_REQUEST_CODE
}

/**
 * The query parameters of the counts route.
 *
 * The only parameter it takes is the dossiers tab's "show records without a
 * dossier" toggle, which changes what that tab *is* rather than narrowing it,
 * so its badge and duplicate figure follow it (`GetDirectoryCountsController`).
 * Off is the API default and sends no key at all.
 *
 * @param {{showWithoutDossier?: boolean}} [state]
 * @returns {{show_without_dossier?: boolean}}
 */
export function buildCountsParams({ showWithoutDossier = false } = {}) {
  return showWithoutDossier ? { show_without_dossier: true } : {}
}

/**
 * The "show records without a dossier" toggle as the dossiers tab last left
 * it, read from the per-route view cache `persistViewFiltersStateMixin` keeps.
 *
 * For the callers that refresh the counts while the dossiers tab is not the
 * one on screen — the shell on mount, another tab after a merge — so the
 * dossiers badge is counted on the perimeter that tab will be restored to.
 * The cache key is the tab's route name, which the mixin uses for a route
 * without an id parameter.
 *
 * @param {function(string): (object|null)} getViewState usually
 *   `store.getters['viewFiltersState/getState']`
 * @returns {boolean}
 */
export function persistedShowWithoutDossier(getViewState) {
  const dossiersRouteName = ADDRESS_BOOK_TABS.find(
    (addressBookTab) => addressBookTab.tab === 'dossiers'
  )?.id

  return getViewState(dossiersRouteName)?.showWithoutDossier === true
}

/**
 * The payload of a counts refresh asked from a tab page.
 *
 * On the dossiers tab the toggle is read live: the view cache is only written
 * when the user leaves the tab, so it lags a flip made on it. On the other
 * tabs the dossiers tab is not mounted, and the cache holds the value it will
 * be restored to.
 *
 * @param {{tab: string, showWithoutDossier: boolean, getViewState: function(string): (object|null)}} state
 * @returns {{showWithoutDossier: boolean}}
 */
export function countsPayloadFor({ tab, showWithoutDossier, getViewState }) {
  if (tab === 'dossiers') {
    return { showWithoutDossier: showWithoutDossier === true }
  }

  return { showWithoutDossier: persistedShowWithoutDossier(getViewState) }
}

/**
 * The most records the merge panel accepts at once, the principal included.
 *
 * Mirrors `MergePreviewRequest::MAX_RECORDS` on the API, which refuses a
 * larger preview with a 422 anyway; checking here first spares opening a
 * panel that could only fail.
 *
 * @type {number}
 */
export const DUPLICATE_GROUP_CAP = 100

/**
 * The listing query that fetches one whole duplicate group, or null when there
 * is no group to ask for.
 *
 * The key is sent exactly as the row carried it (`duplicate_key`): the API
 * matches it with `=` on its own stored value, so trimming or folding it here
 * could only make it miss. Nothing else travels with it — the API derives the
 * flags the key implies (duplicates only, and on the dossiers tab the deceased
 * and the people without a dossier), and an explicit `show_without_dossier`
 * would be refused on a contact tab. One page as large as the cap: a larger
 * group is refused before the panel opens, on `meta.total`.
 *
 * @param {{tab: string, duplicateKey: (string|null|undefined)}} state
 * @returns {{tab: string, duplicate_key: string, per_page: number}|null}
 */
export function buildDuplicateGroupParams({ tab, duplicateKey }) {
  if (tab === 'internal' || typeof duplicateKey !== 'string') {
    return null
  }

  if (duplicateKey === '') {
    return null
  }

  return { tab, duplicate_key: duplicateKey, per_page: DUPLICATE_GROUP_CAP }
}

/**
 * What to do with a duplicate group once its fetch answers.
 *
 * - `ignore`: the call was cancelled or superseded (null), or the user has
 *   moved to another tab since the click — the ids belong to the tab left, and
 *   opening the panel under the new one would preview them as the wrong model
 *   (a 422) or, between Messaging and Suppliers, as a valid merge offered
 *   under the wrong tab.
 * - `too_large`: more records than one merge accepts, read on the total.
 * - `too_small`: fewer than two records, nothing to merge (the group changed
 *   since the row was listed).
 * - `open`: open the merge panel on the ids.
 *
 * @param {{group: ({ids: Array<string>, total: number}|null), requestedTab: string, currentTab: string}} state
 * @returns {'ignore'|'too_large'|'too_small'|'open'}
 */
export function duplicateGroupOutcome({ group, requestedTab, currentTab }) {
  if (group === null || requestedTab !== currentTab) {
    return 'ignore'
  }

  if (duplicateGroupExceedsCap(group.total)) {
    return 'too_large'
  }

  if (group.ids.length < 2) {
    return 'too_small'
  }

  return 'open'
}

/**
 * Whether a duplicate group is too large for one merge.
 *
 * Fed the group's `meta.total`, never the length of the page fetched: that
 * page is capped at `DUPLICATE_GROUP_CAP` rows, so its length can never tell a
 * group of 100 from one of 1 550.
 *
 * @param {number} total
 * @returns {boolean}
 */
export function duplicateGroupExceedsCap(total) {
  return total > DUPLICATE_GROUP_CAP
}
